import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Users, Search, ArrowDownAZ, ArrowUpAZ, ArrowDown01, ArrowUp01, Check, X } from 'lucide-react';

/**
 * Modal completo de seleção de grupos: busca, filtros de ordenação, grid 2 colunas.
 * props:
 *   open, onOpenChange
 *   groups: [{id, name, participants_count}]
 *   selectedGroups: [id...]
 *   onToggle(id), onSelectAll(ids | null)  -> passa a lista de ids visíveis pra marcar todos, ou null pra desmarcar
 */
export default function GroupSelectorModal({ open, onOpenChange, groups, selectedGroups, onToggle, onSetSelection }) {
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState('az'); // az | za | mais | menos

  const filtrados = useMemo(() => {
    let arr = [...(groups || [])];
    const q = busca.trim().toLowerCase();
    if (q) arr = arr.filter((g) => (g.name || '').toLowerCase().includes(q));
    const nome = (g) => (g.name || '').toLowerCase();
    const memb = (g) => g.participants_count || 0;
    if (ordem === 'az') arr.sort((a, b) => nome(a).localeCompare(nome(b)));
    else if (ordem === 'za') arr.sort((a, b) => nome(b).localeCompare(nome(a)));
    else if (ordem === 'mais') arr.sort((a, b) => memb(b) - memb(a));
    else if (ordem === 'menos') arr.sort((a, b) => memb(a) - memb(b));
    return arr;
  }, [groups, busca, ordem]);

  const idsVisiveis = filtrados.map((g) => g.id);
  const todosVisiveisSelecionados = idsVisiveis.length > 0 && idsVisiveis.every((id) => selectedGroups.includes(id));

  const marcarTodosVisiveis = () => {
    if (todosVisiveisSelecionados) {
      // desmarca só os visíveis
      onSetSelection(selectedGroups.filter((id) => !idsVisiveis.includes(id)));
    } else {
      // adiciona os visíveis aos já selecionados (sem duplicar)
      const novo = new Set(selectedGroups);
      idsVisiveis.forEach((id) => novo.add(id));
      onSetSelection([...novo]);
    }
  };

  const ordemBtns = [
    { v: 'az', label: 'A-Z', icon: ArrowDownAZ },
    { v: 'za', label: 'Z-A', icon: ArrowUpAZ },
    { v: 'mais', label: 'Mais membros', icon: ArrowDown01 },
    { v: 'menos', label: 'Menos membros', icon: ArrowUp01 },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] flex flex-col p-0 gap-0">
        <DialogHeader className="p-4 pb-3 border-b border-border/60">
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" /> Selecionar grupos
            <span className="text-sm font-normal text-muted-foreground">({selectedGroups.length} selecionado{selectedGroups.length !== 1 ? 's' : ''})</span>
          </DialogTitle>
        </DialogHeader>

        {/* Busca + ordenação */}
        <div className="p-4 pb-2 space-y-2 border-b border-border/40">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar grupo pelo nome..." className="pl-9" />
            {busca && (
              <button onClick={() => setBusca('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {ordemBtns.map(({ v, label, icon: Icon }) => (
              <button
                key={v}
                onClick={() => setOrdem(v)}
                className={`flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors ${
                  ordem === v ? 'bg-primary/15 border-primary/40 text-primary' : 'bg-muted/30 border-transparent text-muted-foreground hover:border-border'
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
            <button
              onClick={marcarTodosVisiveis}
              className="ml-auto flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border border-primary/30 text-primary hover:bg-primary/10"
            >
              <Check className="w-3.5 h-3.5" /> {todosVisiveisSelecionados ? 'Desmarcar' : 'Marcar'} exibidos ({idsVisiveis.length})
            </button>
          </div>
        </div>

        {/* Grid 2 colunas */}
        <div className="flex-1 overflow-y-auto p-4 pt-3">
          {filtrados.length === 0 ? (
            <p className="text-center py-10 text-muted-foreground text-sm">
              {busca ? `Nenhum grupo com "${busca}"` : 'Nenhum grupo encontrado'}
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {filtrados.map((group) => {
                const isSelected = selectedGroups.includes(group.id);
                return (
                  <div
                    key={group.id}
                    onClick={() => onToggle(group.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                      isSelected ? 'bg-primary/15 border border-primary/30' : 'bg-muted/30 border border-transparent hover:border-border'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-primary-foreground" strokeWidth={3} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">{group.name}</p>
                      <p className="text-xs text-muted-foreground">{group.participants_count} participantes</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Rodapé */}
        <div className="p-4 border-t border-border/60 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{filtrados.length} de {groups.length} grupos</span>
          <Button onClick={() => onOpenChange(false)}>Pronto ({selectedGroups.length})</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
